import { DatePicker, Image, Input } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import React, { useEffect, useState } from "react";
import {
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
} from "../../../redux/Api/CampaignManagment/cMCampaignsApiSlice";
import CountriesDropdown from "../../CountriesDropdown";
import CampaignTypesDropdown from "../../CampaignTypesDropdown";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import Loader from "../../../pages/pages/loader";

const CampaignFormStep1 = ({ campaign, onNext, isEdit }) => {
  const [createCampaign, { isLoading: isCreating }] =
    useCreateCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] =
    useUpdateCampaignMutation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(
    campaign?.campaign_poster_path || null,
  );

  // Helper to extract ID from nested objects
  const getInitialValue = (field) => {
    const val = campaign?.[field];
    if (val && typeof val === "object" && "id" in val) {
      return val.id;
    }
    return val || null;
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      campaign_name: campaign?.campaign_name || null,
      campaign_actors: campaign?.campaign_actors || null,
      sample_of_work: campaign?.sample_of_work || null,
      sample_of_pirated_url: campaign?.sample_of_pirated_url || null,
      number_of_seasons: campaign?.number_of_seasons || null,
      number_of_episodes: campaign?.number_of_episodes || null,
      campaign_duration: campaign?.campaign_duration || null,
      campaign_release_date: campaign?.campaign_release_date || null,
      campaign_type: getInitialValue("campaign_type"),
      country: getInitialValue("country"),
      campaign_rights_owner: campaign?.campaign_rights_owner || null,
      asset_id: campaign?.asset_id || null,
      campaign_poster_path: null,
    },
    validationSchema: Yup.object({
      campaign_name: Yup.string().required("Please Enter Campaign Name"),
      asset_id: Yup.string().nullable(),
      campaign_type: Yup.string().required("Please Choose Campaign Type"),
      country: Yup.string().required("Please Choose Country"),
      number_of_seasons: Yup.number().nullable(),
      number_of_episodes: Yup.number().nullable(),
      sample_of_work: Yup.string().nullable(),
      sample_of_pirated_url: Yup.string().url().nullable(),
      campaign_actors: Yup.string().nullable(),
      campaign_release_date: Yup.date().nullable(),
    }),
    onSubmit: async (values) => {
      // CREATE MODE
      if (!isEdit || !campaign?.id) {
        const formData = new FormData();
        Object.entries(values).forEach(([key, val]) => {
          if (val !== null && val !== undefined) {
            formData.append(key, val);
          }
        });
        if (file) {
          formData.append("campaign_poster_path", file);
        }

        try {
          const result = await createCampaign(formData).unwrap();
          toast.success("Campaign created successfully");
          onNext(result.id);
        } catch (error) {
          if (error?.data && typeof error.data === "object") {
            const errorMessages = Object.values(error.data).flat();
            errorMessages.forEach((message) => {
              toast.error(message);
            });
          } else {
            console.error(error);
            toast.error("Failed to save campaign");
          }
        }
        return;
      }

      const changedFields = {};
      let hasChanges = false;

      // Helper to normalize values for comparison
      const normalize = (val, key) => {
        if (val === null || val === undefined || val === "") return "";
        if (key === "campaign_release_date") {
          return dayjs(val).format("YYYY-MM-DD");
        }
        if (typeof val === "object" && val !== null && "id" in val)
          return String(val.id);
        return String(val).trim();
      };

      // Fields to check
      const fields = [
        "campaign_name",
        "campaign_actors",
        "sample_of_work",
        "sample_of_pirated_url",
        "number_of_seasons",
        "number_of_episodes",
        "campaign_duration",
        "campaign_release_date",
        "campaign_type",
        "country",
        "campaign_rights_owner",
        "asset_id",
      ];

      fields.forEach((key) => {
        const formNorm = normalize(values[key], key);
        const apiNorm = normalize(campaign?.[key], key);

        if (formNorm !== apiNorm) {
          hasChanges = true;
          changedFields[key] =
            values[key] !== "" && values[key] !== undefined
              ? values[key]
              : null;
        }
      });

      if (file) {
        hasChanges = true;
        changedFields.campaign_poster_path = file;
      }

      if (!hasChanges) {
        toast.info("No changes detected");
        onNext(campaign.id);
        return;
      }

      const formData = new FormData();
      Object.entries(changedFields).forEach(([key, val]) => {
        formData.append(key, val ?? "");
      });

      try {
        await updateCampaign({ data: formData, id: campaign.id }).unwrap();
        toast.success("Campaign updated successfully");
        onNext(campaign.id);
      } catch (error) {
        if (error?.data && typeof error.data === "object") {
          const errorMessages = Object.values(error.data).flat();
          errorMessages.forEach((message) => {
            toast.error(message);
          });
        } else {
          console.error(error);
          toast.error("Failed to save campaign");
        }
      }
    },
  });

  useEffect(() => {
    if (!file) {
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const handleNext = async () => {
    const errors = await validation.validateForm();
    if (Object.keys(errors).length !== 0) {
      Object.keys(errors).forEach((key) => {
        toast.error(errors[key]);
      });
      return;
    }
    validation.handleSubmit();
  };

  return (
    <>
      <div>
        <Loader isLoading={isCreating || isUpdating} />
        <div className="flex w-full gap-8">
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Campaign Name</label>
            <Input
              type="text"
              name="campaign_name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.campaign_name}
            />
            {validation.touched.campaign_name &&
            validation.errors.campaign_name ? (
              <p className="text-danger">{validation.errors.campaign_name}</p>
            ) : null}
          </div>
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Asset ID</label>
            <Input
              type="text"
              name="asset_id"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.asset_id}
            />
            {validation.touched.asset_id && validation.errors.asset_id ? (
              <p className="text-danger">{validation.errors.asset_id}</p>
            ) : null}
          </div>
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Campaign Actors</label>
            <Input
              type="text"
              name="campaign_actors"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.campaign_actors}
            />
            {validation.touched.campaign_actors &&
            validation.errors.campaign_actors ? (
              <p className="text-danger">{validation.errors.campaign_actors}</p>
            ) : null}
          </div>
        </div>

        <div className="flex w-full gap-8">
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Work Sample</label>
            <Input
              type="text"
              name="sample_of_work"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.sample_of_work}
            />
            {validation.touched.sample_of_work &&
            validation.errors.sample_of_work ? (
              <p className="text-danger">{validation.errors.sample_of_work}</p>
            ) : null}
          </div>
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Pirated Url Sample</label>
            <Input
              type="text"
              name="sample_of_pirated_url"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.sample_of_pirated_url}
            />
            {validation.touched.sample_of_pirated_url &&
            validation.errors.sample_of_pirated_url ? (
              <p className="text-danger">
                {validation.errors.sample_of_pirated_url}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex w-full gap-8">
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Number Of Seasons</label>
            <Input
              type="number"
              name="number_of_seasons"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.number_of_seasons}
            />
            {validation.touched.number_of_seasons &&
            validation.errors.number_of_seasons ? (
              <p className="text-danger">
                {validation.errors.number_of_seasons}
              </p>
            ) : null}
          </div>
          <div className="mb-3 flex-1 customInput">
            <label className="form-label">Number Of Episodes</label>
            <Input
              type="number"
              name="number_of_episodes"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.number_of_episodes}
            />
            {validation.touched.number_of_episodes &&
            validation.errors.number_of_episodes ? (
              <p className="text-danger">
                {validation.errors.number_of_episodes}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex w-full gap-8">
          <div className="mb-3 customeDate flex-1">
            <label className="form-label">Campaign Release Date</label>
            <br />
            <DatePicker
              style={{ width: "100%" }}
              name="campaign_release_date"
              onChange={(e, date) =>
                validation.setFieldValue("campaign_release_date", date)
              }
              onBlur={validation.handleBlur}
              value={
                validation.values.campaign_release_date
                  ? dayjs(validation.values.campaign_release_date)
                  : null
              }
            />
            {validation.touched.campaign_release_date &&
            validation.errors.campaign_release_date ? (
              <p className="text-danger">
                {validation.errors.campaign_release_date}
              </p>
            ) : null}
          </div>
          <div className="mb-3 customInput flex-1">
            <label className="form-label">Campaign Rights Owner</label>
            <Input
              type="text"
              name="campaign_rights_owner"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.campaign_rights_owner}
            />
            {validation.touched.campaign_rights_owner &&
            validation.errors.campaign_rights_owner ? (
              <p className="text-danger">
                {validation.errors.campaign_rights_owner}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex w-full gap-8">
          <div className="mb-3 customSelect flex-1">
            <CampaignTypesDropdown
              onChange={validation.setFieldValue}
              onBlur={validation.handleBlur}
              value={validation.values.campaign_type}
            />
            {validation.touched.campaign_type &&
            validation.errors.campaign_type ? (
              <p className="text-danger">{validation.errors.campaign_type}</p>
            ) : null}
          </div>
          <div className="mb-3 customSelect flex-1">
            <CountriesDropdown
              onChange={validation.setFieldValue}
              onBlur={validation.handleBlur}
              value={validation.values.country}
            />
            {validation.touched.country && validation.errors.country ? (
              <p className="text-danger">{validation.errors.country}</p>
            ) : null}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Upload Campaign Poster</label>
          <div className="flex gap-4 customInput">
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".png, .jpg, .jpeg"
              style={{ height: "40px" }}
            />
            {preview && (
              <Image
                className="border-1"
                style={{ width: "200px" }}
                src={preview}
              />
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-8 mt-4">
        <button disabled className="btn btn-theme">
          Previous
        </button>
        <button onClick={handleNext} className="btn btn-theme">
          Save/Next
        </button>
      </div>
    </>
  );
};

export default CampaignFormStep1;
